import React, { useState, useMemo, useCallback } from "react";
import {
  getBrokerageFlags,
  filterExperienceFields,
} from "../../../../utils/brokerageFields";

// ── Constants ──────────────────────────────────────────────────────────────────

const EXPERIENCE_FIELDS = [
  { key: "bonds", label: "Bonds" },
  { key: "stocks", label: "Stocks" },
  { key: "alternative_investments", label: "Alternative Investments" },
  { key: "variable_annuities", label: "Variable Annuities" },
  { key: "mutual_funds", label: "Mutual Funds" },
  { key: "options", label: "Options" },
  { key: "partnerships", label: "Partnerships" },
];

// ── InvestmentExperienceForm ───────────────────────────────────────────────────

const InvestmentExperienceForm = ({
  householdData,
  fieldData,
  missingFields,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const existing = useMemo(() => {
    return fieldData?.investor_experience || {};
  }, [fieldData]);

  const brokerageFlags = useMemo(
    () => getBrokerageFlags(householdData),
    [householdData]
  );

  // Determine which fields to show (where value is null/undefined),
  // gating the brokerage-conditional experience fields entirely.
  const fieldsToShow = useMemo(() => {
    return filterExperienceFields(EXPERIENCE_FIELDS, brokerageFlags, householdData?.accounts).filter(
      (field) => existing[field.key] === null || existing[field.key] === undefined
    );
  }, [existing, brokerageFlags, householdData]);

  // Initialize formData with empty string for each field to show
  const [formData, setFormData] = useState(() => {
    const initial = {};
    fieldsToShow.forEach((field) => {
      initial[field.key] = "";
    });
    return initial;
  });

  const [errors, setErrors] = useState({});

  // ── Handlers ───────────────────────────────────────────────────────────────────

  const handleFieldChange = useCallback((key, value) => {
    // Strip leading zeros
    let cleanedValue = value;
    if (value && value.length > 1 && value.startsWith("0")) {
      cleanedValue = value.replace(/^0+/, "") || "0";
    }

    // Clamp to 100
    if (cleanedValue && cleanedValue !== "") {
      const numValue = parseInt(cleanedValue, 10);
      if (numValue > 100) {
        cleanedValue = "100";
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: cleanedValue,
    }));

    // Clear error when user starts typing
    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: null,
      }));
    }
  }, [errors]);

  const handleKeyDown = (e) => {
    // Block e, E, -
    if (e.key === "e" || e.key === "E" || e.key === "-") {
      e.preventDefault();
    }
  };

  const validateAndBuildPayload = useCallback(() => {
    const experience = { ...existing };
    fieldsToShow.forEach((field) => {
      const raw = formData[field.key];
      const value = typeof raw === "string" ? raw.trim() : raw;
      if (value === "" || value === null || value === undefined) return;
      const numValue = parseInt(String(value), 10);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        experience[field.key] = numValue;
      }
    });

    return {
      household: {
        investor_experience: experience,
      },
    };
  }, [formData, fieldsToShow, existing]);

  const handleSave = useCallback(() => {
    const payload = validateAndBuildPayload();
    onSave(payload);
  }, [validateAndBuildPayload, onSave]);

  const handleSaveAndExit = useCallback(() => {
    const payload = validateAndBuildPayload();
    onSaveAndExit(payload);
  }, [validateAndBuildPayload, onSaveAndExit]);

  // ── Render ─────────────────────────────────────────────────────────────────────

  if (fieldsToShow.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All investment experience fields have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          {fieldsToShow.length} Required field{fieldsToShow.length !== 1 ? "s" : ""} missing
        </span>
      </div>

      {/* Form grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {fieldsToShow.map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}{" "}
              <span className="text-red-500">*</span>
              <span className="text-gray-500 font-normal"> (years)</span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData[field.key]}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              onKeyDown={handleKeyDown}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter years"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${
                errors[field.key]
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300"
              }`}
            />
            {errors[field.key] && (
              <p className="text-xs text-red-600 mt-1">{errors[field.key]}</p>
            )}
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
        <button
          onClick={handleSaveAndExit}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          Save & Exit
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 disabled:bg-emerald-400 disabled:cursor-not-allowed transition-colors"
        >
          {saving && (
            <svg
              className="w-4 h-4 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v.01M17.657 6.343l-.707.707M20 12h-.01M17.657 17.657l-.707-.707M12 20v.01M6.343 17.657l.707-.707M4 12h.01M6.343 6.343l.707.707"
              />
            </svg>
          )}
          <span>{nextLabel || "Save & Next →"}</span>
        </button>
      </div>
    </div>
  );
};

export default InvestmentExperienceForm;
